import posthog from 'posthog-js';
import type { Analytics } from 'firebase/analytics';
import { getAnalytics, isSupported, logEvent, setUserId } from 'firebase/analytics';
import firebaseConfig from '../../firebase-applet-config.json';
import { app } from './firebaseApp';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://a.lexgat.com';

let ga4Analytics: Analytics | null = null;

function isAutomatedBrowser() {
  return typeof navigator !== 'undefined' && !!(navigator as Navigator & { webdriver?: boolean }).webdriver;
}

async function initGa4() {
  if (typeof window === 'undefined') return;
  if (isAutomatedBrowser()) return;
  if (!firebaseConfig.measurementId?.trim()) {
    if (import.meta.env.DEV) {
      console.warn('GA4 measurementId not set — Google Analytics disabled');
    }
    return;
  }
  try {
    if (!(await isSupported())) return;
    ga4Analytics = getAnalytics(app);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('GA4 init failed:', err);
    }
  }
}

export function initAnalytics() {
  if (typeof window === 'undefined') return;
  if (isAutomatedBrowser()) return;
  if (!POSTHOG_KEY) {
    if (import.meta.env.DEV) {
      console.warn('PostHog key not set — analytics disabled');
    }
  } else {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: {
        dom_event_allowlist: ['click', 'submit'],
      },
      capture_performance: {
        web_vitals: true,
      },
    });
  }

  void initGa4();
}

export function trackPageView(path: string) {
  if (typeof window === 'undefined' || isAutomatedBrowser()) return;

  if (POSTHOG_KEY) {
    posthog.capture('$pageview', { path });
  }

  if (ga4Analytics) {
    logEvent(ga4Analytics, 'page_view', {
      page_path: path,
      page_location: `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`,
      page_title: document.title,
    });
  }
}

export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined' || isAutomatedBrowser()) return;

  if (POSTHOG_KEY) {
    posthog.identify(userId, properties);
  }

  if (ga4Analytics) {
    setUserId(ga4Analytics, userId);
  }
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (typeof window === 'undefined' || isAutomatedBrowser()) return;

  if (POSTHOG_KEY) {
    posthog.capture(eventName, properties);
  }

  if (ga4Analytics) {
    logEvent(ga4Analytics, eventName, properties);
  }
}
